import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Paper, 
  Typography, 
  CircularProgress, 
  Card, 
  CardContent, 
  CardActions,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { Send as SendIcon, Download as DownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';

const AiImagePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const generateImage = async () => {
    if (!prompt.trim()) {
      setError('Por favor ingresa una descripción para generar la imagen');
      setOpenSnackbar(true);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    // Verificar que la API key esté configurada
    const apiKey = process.env.REACT_APP_STABILITY_API_KEY || 'sk-1nzI2YucDMZub3FuXQVVJdUBZLOLrPJgGeGcafel0VVDvXqI';
    
    if (!apiKey) {
      setError('Error: No se encontró la API key. Por favor configura la variable de entorno REACT_APP_STABILITY_API_KEY');
      setOpenSnackbar(true);
      setIsLoading(false);
      return;
    }
    
    try {
      console.log('Iniciando solicitud a la API...');
      console.log('Usando API Key:', apiKey ? '✅ Configurada' : '❌ No configurada');
      
      const response = await fetch('https://api.stability.ai/v1/generation/stable-diffusion-xl-1024-v1-0/text-to-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          text_prompts: [
            {
              text: prompt,
              weight: 1,
            },
          ],
          cfg_scale: 7,
          height: 1024,
          width: 1024,
          steps: 30,
          samples: 1,
        }),
      });

      console.log('Respuesta recibida:', response.status, response.statusText);
      
      if (!response.ok) {
        let errorMessage = 'Error al generar la imagen';
        try {
          const errorData = await response.json();
          console.error('Detalles del error:', errorData);
          errorMessage = errorData.message || errorData.error?.message || errorMessage;
        } catch (e) {
          console.error('Error al parsear respuesta de error:', e);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Datos de la respuesta:', data);
      
      if (!data.artifacts || !data.artifacts[0]?.base64) {
        throw new Error('Formato de respuesta inesperado de la API');
      }
      
      setGeneratedImage(`data:image/png;base64,${data.artifacts[0].base64}`);
      
    } catch (err) {
      console.error('Error generating image:', err);
      setError('Error al generar la imagen. Intenta nuevamente.');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `ai-image-${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = () => {
    setPrompt('');
    setGeneratedImage(null);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Generador de Imágenes con IA
      </Typography>
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
          <TextField
            fullWidth
            variant="outlined"
            label="Describe la imagen que deseas generar"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={isLoading}
            placeholder="Ej: Un gato astronauta en el espacio, estilo dibujo animado"
            multiline
            rows={2}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={generateImage}
            disabled={isLoading || !prompt.trim()}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{ minWidth: 200, height: 56 }}
          >
            {isLoading ? 'Generando...' : 'Generar Imagen'}
          </Button>
        </Box>
      </Paper>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
            <Typography variant="h6">Generando tu imagen...</Typography>
            <Typography color="textSecondary">Esto puede tomar unos segundos</Typography>
          </Box>
        </Box>
      )}

      {generatedImage && (
        <Card sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
          <CardContent>
            <img 
              src={generatedImage} 
              alt="AI Generated" 
              style={{ 
                width: '100%', 
                height: 'auto',
                borderRadius: theme.shape.borderRadius
              }} 
            />
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              sx={{ mr: 1 }}
            >
              Nueva Imagen
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Descargar
            </Button>
          </CardActions>
        </Card>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AiImagePage;
